// #### Unloading handling

var ignoreClick = false

import { GenericConfirmDialog } from "./hooks/generic_confirm_dialog_hook"
import FormSnapshotter from "./form_snapshotter"

function findClosestElement(target, selector) {
  return target instanceof Element ? target.closest(selector) : null
}

function confirmationCandidates(selector) {
  if (!selector) return document.querySelectorAll("[data-unload-confirm]")

  const roots = document.querySelectorAll(selector)
  const elements = []

  for (const root of roots) {
    if (root.matches("[data-unload-confirm]")) elements.push(root)
    elements.push(...root.querySelectorAll("[data-unload-confirm]"))
  }

  return elements
}

export const Unload = {
  init() {
    window.addEventListener("phoenix.link.click", Unload.interceptClick)
    window.addEventListener("beforeunload", Unload.interceptUnload)
  },

  // Intercepts clicks on elements with `data-unload-verify` attribute
  interceptClick(event) {
    if (ignoreClick) return

    const link = findClosestElement(event.target, "[data-unload-verify]")
    if (!link) return

    const selector = link.dataset.unloadVerify

    const needConfirmation = Unload.requiresConfirmation(selector)
    if (!needConfirmation) return

    event.stopPropagation()
    event.preventDefault()

    GenericConfirmDialog.confirm(
      Unload.confirmationArgs(needConfirmation, link),
      () => {
        ignoreClick = true
        link.click()
        ignoreClick = false
      },
    )
  },

  // Similar idea to `interceptClick`, but for looking in whole document.
  // If default is prevented, browser will present a confirmation dialog.
  interceptUnload(event) {
    // When LiveView has lost connection (server restart, deploy, branch switch),
    // its failsafe tries to reload the page repeatedly. Don't block those reloads --
    // the form state is already gone server-side, so the confirmation is pointless
    // and creates a loop of "Reload site?" dialogs the user must dismiss repeatedly.
    if (window.liveSocket?.isConnected?.() === false) return

    const needConfirmation = Unload.requiresConfirmation()
    if (needConfirmation) event.preventDefault()
  },

  // Returns any element that requires confirmation before being unloaded
  // Optionally pass a selector to limit search within that element
  requiresConfirmation(selector) {
    const elements = confirmationCandidates(selector)

    for (const element of elements) {
      // Retained inline drafts still need protection in inactive tab panels.
      // Other hidden ancestors (including a dismissed modal) remain excluded.
      const retained = element.closest('[data-unload-retained-draft="true"]')
      let hidden = element.closest("[hidden]")
      while (retained && hidden?.getAttribute("role") === "tabpanel") {
        hidden = hidden.parentElement?.closest("[hidden]")
      }
      if (hidden || element.closest("dialog:not([open])")) continue

      const value = element.dataset.unloadConfirm

      // Handle "check-snapshot" - use FormSnapshotter to determine if changed
      if (value === "check-snapshot") {
        // Element must be a form with an id for snapshot to work
        if (element.tagName !== "FORM" || !element.id) {
          console.warn("Skipping check-snapshot for non-form element:", element)
          continue
        }

        if (FormSnapshotter.changed(element)) return element
        continue
      }

      // For any other value (including "true", "false", or empty string),
      // the presence of the attribute means confirmation is needed
      // unless explicitly set to the string "false"
      if (value !== "false") return element
    }

    return null
  },

  // Extracts confirmation dialog args from the element datasets.
  // When `source` (the clicked link) is provided, its attributes take priority
  // over `needConfirmation` (the form element). This allows links to customize
  // the dialog text while the form controls whether confirmation is needed.
  confirmationArgs(needConfirmation, source) {
    const primary = source ? source.dataset : needConfirmation.dataset
    const fallback = source ? needConfirmation.dataset : {}

    const title = primary.unloadConfirmTitle || fallback.unloadConfirmTitle
    const prompt = primary.unloadConfirmPrompt || fallback.unloadConfirmPrompt
    const submit = primary.unloadConfirmSubmit || fallback.unloadConfirmSubmit
    const cancel = primary.unloadConfirmCancel || fallback.unloadConfirmCancel
    const reverseButtons =
      (primary.unloadConfirmReverseButtons ||
        fallback.unloadConfirmReverseButtons) === "true"

    return {
      title: title || window.I18n?.["Unsaved changes"] || "Unsaved changes",
      prompt:
        prompt ||
        window.I18n?.[
          "You have some changes that have not been saved and will be lost if you proceed. Are you sure?"
        ] ||
        "You have some changes that have not been saved and will be lost if you proceed. Are you sure?",
      submit: submit || window.I18n?.["Proceed"] || "Proceed",
      cancel: cancel || undefined,
      reverseButtons,
    }
  },
}
Unload.init()
